import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { UNSAFE_LocationContext as LocationContext } from 'react-router-dom';

import { findTarget } from './targets';
import { WINDOW_MEDIA } from '../../config/windowClasses';

/** How long a step waits for its target before it is skipped. */
export const TARGET_WAIT_MS = 1500;
/** How long a typed value sits still before a `type` step moves on. */
export const TYPE_SETTLE_MS = 700;
/** The compact window class (platform-standards §18), where the shell draws the tab bar. */
export const PHONE_QUERY = WINDOW_MEDIA.compact;

/**
 * The current path. Inside a router it is the router's; outside one (a
 * component test, a probe page) it is the window's, kept fresh on back and
 * forward. A `navigate` step needs a router to see the app move.
 */
export function usePathname() {
	const location = useContext(LocationContext);
	const [windowPath, setWindowPath] = useState(() => (typeof window === 'undefined' ? '/' : window.location.pathname));
	useEffect(() => {
		if (location || typeof window === 'undefined') return undefined;
		const update = () => setWindowPath(window.location.pathname);
		window.addEventListener('popstate', update);
		return () => window.removeEventListener('popstate', update);
	}, [location]);
	return location && location.location ? location.location.pathname : windowPath;
}

/** Is the viewport a phone's? Follows the window as it changes. */
export function useDocked() {
	const read = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(PHONE_QUERY).matches;
	const [docked, setDocked] = useState(read);
	useEffect(() => {
		if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
		const query = window.matchMedia(PHONE_QUERY);
		const update = () => setDocked(Boolean(query.matches));
		update();
		if (query.addEventListener) query.addEventListener('change', update);
		else if (query.addListener) query.addListener(update);
		return () => {
			if (query.removeEventListener) query.removeEventListener('change', update);
			else if (query.removeListener) query.removeListener(update);
		};
	}, []);
	return docked;
}

/**
 * The element a step points at, followed as the page changes. `element` is
 * null while there is none; `missing` turns true once it has been absent for
 * `waitMs`, which is a target that is not coming (or has gone). A target that
 * is swapped for a new node (React remounting it) is picked up at once and
 * never counts as missing. A step with no target is never missing.
 */
export function useTargetElement(name, resetKey, waitMs = TARGET_WAIT_MS) {
	// The answer carries the key it was found for, so the render right after a
	// step change never pairs the new step with the last step's element.
	const key = `${name || ''}|${resetKey}`;
	const [found, setFound] = useState({ key: null, element: null, missing: false });

	useLayoutEffect(() => {
		if (!name) {
			setFound({ key, element: null, missing: false });
			return undefined;
		}
		let timer = null;
		let current = null;
		const publish = (element, missing) => setFound({ key, element, missing });
		const look = () => {
			const element = findTarget(name);
			if (element === current) return;
			current = element;
			clearTimeout(timer);
			timer = null;
			if (element) {
				publish(element, false);
			} else {
				publish(null, false);
				timer = setTimeout(() => publish(null, true), waitMs);
			}
		};
		current = findTarget(name);
		if (current) {
			publish(current, false);
		} else {
			publish(null, false);
			timer = setTimeout(() => publish(null, true), waitMs);
		}
		const observer = typeof MutationObserver === 'function' ? new MutationObserver(look) : null;
		if (observer) {
			observer.observe(document.body, {
				subtree: true,
				childList: true,
				attributes: true,
				attributeFilter: ['style', 'class', 'hidden', 'data-tour'],
			});
		}
		return () => {
			clearTimeout(timer);
			if (observer) observer.disconnect();
		};
	}, [key, name, waitMs]);

	return found.key === key ? { element: found.element, missing: found.missing } : { element: null, missing: false };
}

const sameRect = (a, b) => a && b && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;

/**
 * The element's rectangle in viewport pixels, kept current while it scrolls,
 * resizes, animates in or is moved by a layout shift: it is read every frame,
 * and state changes only when the numbers do.
 */
export function useViewportRect(element) {
	const [held, setHeld] = useState({ element: null, rect: null });
	useLayoutEffect(() => {
		if (!element) return undefined;
		const setRect = (rect) => setHeld({ element, rect });
		let last = null;
		let frame = null;
		const read = () => {
			const box = element.getBoundingClientRect();
			const next = { top: box.top, left: box.left, width: box.width, height: box.height };
			if (!sameRect(last, next)) {
				last = next;
				setRect(next);
			}
		};
		read();
		if (typeof requestAnimationFrame !== 'function') return undefined;
		const tick = () => {
			read();
			frame = requestAnimationFrame(tick);
		};
		frame = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(frame);
	}, [element]);
	// A rectangle read for another element is not this one's.
	return held.element === element ? held.rect : null;
}

/** The window's inner size, following a resize. */
export function useViewportSize() {
	const read = () => ({ width: window.innerWidth, height: window.innerHeight });
	const [size, setSize] = useState(read);
	useEffect(() => {
		const update = () => setSize((previous) => {
			const next = read();
			return previous.width === next.width && previous.height === next.height ? previous : next;
		});
		window.addEventListener('resize', update);
		return () => window.removeEventListener('resize', update);
	}, []);
	return size;
}

/** An element's size, kept current (the step card grows and shrinks with its text). */
export function useElementSize(ref, resetKey) {
	const [size, setSize] = useState({ width: 0, height: 0 });
	const last = useRef(size);
	useLayoutEffect(() => {
		const element = ref.current;
		if (!element) return undefined;
		const measure = () => {
			const box = element.getBoundingClientRect();
			if (box.width === last.current.width && box.height === last.current.height) return;
			last.current = { width: box.width, height: box.height };
			setSize(last.current);
		};
		measure();
		if (typeof ResizeObserver !== 'function') return undefined;
		const observer = new ResizeObserver(measure);
		observer.observe(element);
		return () => observer.disconnect();
	}, [ref, resetKey]);
	return size;
}
